import type {
  AppLoadContext,
  CookieSerializeOptions,
} from "@remix-run/cloudflare";
import { createServerClient, parse, serialize, createBrowserClient } from "@supabase/ssr";
import type { DbEnv, EnvOriginSchema } from "~/utils/env";
import { environmentBrowsor, EnvSchema } from "~/utils/env";
import type { Database } from "~/types/supabase";
import type { Input } from "valibot";
import { parse as vParse } from 'valibot';

export type SupabaseSSRClientType = ReturnType<typeof createServerClient<Database>>

export const stripeKeyEnv = (env: Input<typeof EnvOriginSchema>, dbEnv: DbEnv | undefined) => {
  return dbEnv === "prd"
    ? {
      STRIPE_PUBLIC_KEY: env.STRIPE_KEY_PUBLIC_PRD,
      STRIPE_SECRET_KEY: env.STRIPE_KEY_SECRET_PRD,
      STRIPE_WEBHOOK_SECRET: env.STRIPE_WEBHOOK_SECRET_PRD,
    }
    : dbEnv === "dev"
      ? {
        STRIPE_PUBLIC_KEY: env.STRIPE_KEY_PUBLIC_TEST,
        STRIPE_SECRET_KEY: env.STRIPE_KEY_SECRET_TEST,
        STRIPE_WEBHOOK_SECRET: env.STRIPE_WEBHOOK_SECRET_DEV,
      }
      : {
        STRIPE_PUBLIC_KEY: env.STRIPE_KEY_PUBLIC_LOCAL,
        STRIPE_SECRET_KEY: env.STRIPE_KEY_SECRET_LOCAL,
        STRIPE_WEBHOOK_SECRET: env.STRIPE_WEBHOOK_SECRET_TEST,
      };
};

export const envURL = (env: Input<typeof EnvOriginSchema>, dbEnv: DbEnv | undefined) => {
  return dbEnv === "prd"
    ? {
      R2_PUBLIC_URL: env.R2_PUBLIC_URL_PRD,
      WORKER_URL: env.WORKER_URL_PRD,
      SITE_URL: env.SITE_URL_PRD,

    }
    : dbEnv === "dev"
      ? {
        R2_PUBLIC_URL: env.R2_PUBLIC_URL_DEV,
        WORKER_URL: env.WORKER_URL_DEV,
        SITE_URL: env.SITE_URL_DEV,
      }
      : {
        R2_PUBLIC_URL: env.R2_PUBLIC_URL_DEV,
        WORKER_URL: env.WORKER_URL_LOCAL,
        SITE_URL: env.SITE_URL_LOCAL,
      };
};
export const dbConfig = (env: Input<typeof EnvOriginSchema>, dbEnv: DbEnv | undefined) => {
  return dbEnv === "prd"
    ? {
      url: env.SUPABASE_URL,
      anonKey: env.SUPABASE_ANON_KEY,
      serviceKey: env.SUPABASE_SERVICE_ROLE,
    }
    : dbEnv === "dev"
      ? {
        url: env.SUPABASE_URL_DEV,
        anonKey: env.SUPABASE_ANON_KEY_DEV,
        serviceKey: env.SUPABASE_SERVICE_ROLE_DEV,
      }
      : {
        url: env.SUPABASE_URL_LOCAL,
        anonKey: env.SUPABASE_ANON_KEY_LOCAL,
        serviceKey: env.SUPABASE_SERVICE_ROLE_LOCAL,

      };
};
export const createSbClientSSR = (
  headers: Headers,
  request: Request,
  context: AppLoadContext,
  isService?: boolean,
  forceEnv?: DbEnv,
) => {
  const cookies = parse(request.headers.get("Cookie") ?? "");

  //let env = context.cloudflare.env as Input<typeof EnvSchema>;
  const env = vParse(EnvSchema, context.cloudflare.env)

  const envDbUse = forceEnv ?? env.DB_ENV;
  const key = !isService ? dbConfig(env, envDbUse).anonKey : dbConfig(env, envDbUse).serviceKey;
  const supabase = createServerClient<Database>(
    dbConfig(env, envDbUse).url,
    key,
    {
      cookies: {
        get(key: string | number) {
          return cookies[key];
        },
        set(
          key: string,
          value: string,
          options: CookieSerializeOptions | undefined
        ) {
          headers.append("Set-Cookie", serialize(key, value, options));
        },
        remove(key: string, options: CookieSerializeOptions | undefined) {
          headers.append("Set-Cookie", serialize(key, "", options));
        },
      },

      auth: {
        ...(isService ? (
          {

            persistSession: false,
            autoRefreshToken: false,
            detectSessionInUrl: false
            /* https://github.com/supabase/auth-helpers/issues/598 */

          }
        ) : {})
      }

    }
  );
  return supabase;
};


export const createSbClientBrowser = () => {
  const env = environmentBrowsor();
  return createBrowserClient<Database>(
    env.SB_URL,
    env.SB_ANON_KEY
  )
}